import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, LogOut, Menu, Moon, Search, Settings, Star, Sun, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { cx } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import { getMarketStatus } from '@/lib/marketStatus';
import { useTheme } from '@/lib/theme';
import { Badge, Kbd } from '@/components/primitives';
import styles from './appShell.module.scss';

const TITLES: Record<string, string> = {
  '/': 'Overview',
  '/markets': 'Markets',
  '/screener': 'Screener',
  '/stocks': 'Stocks',
  '/etfs': 'ETFs',
  '/watchlist': 'Watchlist',
  '/portfolio': 'Portfolio',
  '/alerts': 'Alerts',
  '/pricing': 'Plans',
  '/settings': 'Account',
  '/design': 'Design lab',
  '/login': 'Sign in',
  '/register': 'Create account',
};

function breadcrumb(pathname: string): { section: string; page: string } {
  if (pathname.startsWith('/symbol/')) {
    return { section: 'Universe', page: decodeURIComponent(pathname.replace('/symbol/', '')).toUpperCase() };
  }
  return { section: 'Desk', page: TITLES[pathname] ?? 'Not found' };
}

function initials(name: string, email: string): string {
  const source = name.trim();
  if (source) {
    return source
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export function Topbar({
  onMenu,
  onSearch,
}: {
  onMenu: () => void;
  onSearch: () => void;
}) {
  const { theme, toggle } = useTheme();
  const { user, loading, signOut } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const status = getMarketStatus();
  const crumb = breadcrumb(pathname);

  async function handleSignOut() {
    await signOut();
    toast.success('Signed out');
    navigate('/');
  }

  return (
    <header className={styles.topbar}>
      <div className={styles.topbarLeft}>
        <button type="button" className={styles.iconButton} onClick={onMenu} aria-label="Open menu">
          <Menu size={18} />
        </button>
        <div className={styles.crumbs}>
          <span className={styles.crumbSection}>{crumb.section}</span>
          <span className={styles.crumbSlash}>/</span>
          <span className={styles.crumbPage}>{crumb.page}</span>
        </div>
      </div>

      <div className={styles.topbarRight}>
        <div className={styles.topbarStatus}>
          <span className={cx(styles.statusDot, styles[`status-${status.state}`])} />
          <span>{status.label}</span>
          <span className={styles.topbarStatusDetail}>{status.detail}</span>
        </div>

        <button type="button" className={styles.searchButton} onClick={onSearch}>
          <Search size={14} />
          <span className={styles.searchPlaceholder}>Search symbols</span>
          <span className={styles.searchKeys}>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        <Badge tone="warning" outline className={styles.topbarBadge}>
          Invite only
        </Badge>

        <Link to="/alerts" className={styles.iconButton} aria-label="Alerts">
          <Bell size={17} />
        </Link>

        <button
          type="button"
          className={styles.iconButton}
          onClick={toggle}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
        </button>

        {loading ? null : user ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button type="button" className={styles.accountButton} aria-label="Account menu">
                <span className={styles.avatar}>{initials(user.name, user.email)}</span>
                <span className={styles.accountName}>{user.name.split(' ')[0] || 'Account'}</span>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className={styles.accountMenu}
                sideOffset={7}
                align="end"
                collisionPadding={10}
              >
                <div className={styles.accountMeta}>
                  <strong>{user.name || 'Your account'}</strong>
                  <span>{user.email}</span>
                </div>
                <DropdownMenu.Separator className={styles.menuSeparator} />
                <DropdownMenu.Item className={styles.menuItem} asChild>
                  <Link to="/settings">
                    <Settings size={13} /> Account settings
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={styles.menuItem} asChild>
                  <Link to="/watchlist">
                    <Star size={13} /> Watchlists
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={styles.menuItem} asChild>
                  <Link to="/portfolio">
                    <Wallet size={13} /> Portfolio
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className={styles.menuSeparator} />
                <DropdownMenu.Item
                  className={cx(styles.menuItem, styles.menuItemDanger)}
                  onSelect={handleSignOut}
                >
                  <LogOut size={13} /> Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : (
          <Link to="/login" className={styles.signIn}>
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
}
